import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Cloud, CreditCard, Database, Shield, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  "Cloud Infrastructure": Cloud,
  "Fintech": CreditCard,
  "Data & Analytics": Database,
  "API Infrastructure": Shield,
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products`);
        setProducts(res.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="products-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1647356191320-d7a1f80ca777?auto=format&fit=crop&w=2000&q=80"
            alt="Data visualization"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              Products
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Software for
              <br />
              <span className="text-mint">modern</span>
              <br />
              infrastructure.
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed max-w-2xl opacity-0 animate-fade-up animate-delay-200">
              Proprietary platforms designed to solve complex problems in cloud management, financial services, data analytics, and API infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24 bg-obsidian-light" data-testid="products-grid">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/5 border border-white/5 p-10">
                  <Skeleton className="h-10 w-10 bg-white/10 mb-8" />
                  <Skeleton className="h-8 w-1/2 bg-white/10 mb-4" />
                  <Skeleton className="h-4 w-3/4 bg-white/10 mb-4" />
                  <Skeleton className="h-24 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {products.map((product, idx) => {
                const IconComponent = iconMap[product.category] || Cloud;
                return (
                  <div
                    key={product.id}
                    className={`card-beam p-10 group ${idx === 0 ? 'lg:col-span-2' : ''}`}
                    data-testid={`product-card-${product.slug}`}
                  >
                    <div className="flex items-start justify-between mb-8">
                      <IconComponent className={`${idx === 0 ? 'w-14 h-14' : 'w-10 h-10'} text-mint group-hover:text-coral transition-colors`} />
                      <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-mint/10 text-mint border border-mint/20">
                        {product.status}
                      </span>
                    </div>
                    <h2 className={`${idx === 0 ? 'text-4xl' : 'text-2xl'} font-heading font-bold text-white mb-3 tracking-tight`}>
                      {product.name}
                    </h2>
                    <p className="text-mint text-sm font-medium mb-6">{product.tagline}</p>
                    <p className={`text-slate-400 leading-relaxed mb-8 ${idx === 0 ? 'text-lg max-w-2xl' : ''}`}>
                      {product.description}
                    </p>
                    <div className="border-t border-white/5 pt-8">
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-4">
                        Key Features
                      </p>
                      <div className={`grid ${idx === 0 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'} gap-3`}>
                        {product.features.slice(0, idx === 0 ? 6 : 4).map((feature, fidx) => (
                          <div key={fidx} className="flex items-center gap-3 text-slate-300 text-sm">
                            <span className="w-1.5 h-1.5 bg-mint" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="products-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-card p-12 md:p-20 text-center">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
              Interested in our products?
            </h2>
            <p className="mt-6 text-xl text-slate-400 max-w-xl mx-auto">
              Contact us to learn more about deployment options, pricing, and integration support.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <button 
                  data-testid="products-cta-contact-btn"
                  className="btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors"
                >
                  Contact Sales
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </Link>
              <Link to="/case-studies">
                <button 
                  data-testid="products-cta-case-studies-btn"
                  className="group flex items-center gap-3 bg-transparent border border-white/20 text-white px-8 py-5 font-medium text-sm tracking-wide uppercase hover:bg-white/5 transition-all"
                >
                  View Case Studies
                  <ExternalLink className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
